import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/contexts/AppContext";
import CampaignsTable from "@/components/campaigns/CampaignsTable";
import Button from "@/components/common/Button";
import Card from "@/components/common/Card";
import SearchInput from "@/components/common/SearchInput";
import { usePageTitle } from "@/components/common/AppShell";
import ImportCampaignModal from "@/components/campaigns/ImportCampaignModal";
import TransferCampaignModal from "@/components/campaigns/TransferCampaignModal";
import { Plus, Download } from "lucide-react";

export default function CampaignsPage() {
  usePageTitle("Campaigns");
  const navigate = useNavigate();
  const {
    campaigns,
    campaignsLoading,
    campaignsError,
    refreshCampaigns,
    deleteCampaign,
    approveCampaign,
    currentUserType,
    users,
    segments,
    getCampaignStatistics,
    getCachedCampaignStatistics,
  } = useApp();
  // Stats already loaded this session render at once, without the loading
  // dots or the count-up, and are refreshed in the background.
  const [initialStats] = useState(() => {
    const seed = {};
    for (const c of campaigns) {
      const cached = c.uuid && getCachedCampaignStatistics(c.uuid);
      if (cached) seed[c.uuid] = cached;
    }
    return seed;
  });
  const [campaignStats, setCampaignStats] = useState(initialStats);
  const [cachedUuids, setCachedUuids] = useState(
    () => new Set(Object.keys(initialStats)),
  );
  const [loadingStats, setLoadingStats] = useState(new Set());
  const requestedStatsRef = useRef(new Set());
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [importOpen, setImportOpen] = useState(false);
  const [transferring, setTransferring] = useState(null);

  const tabCounts = useMemo(() => {
    const counts = {
      all: campaigns.length,
      active: 0,
      pending: 0,
      completed: 0,
      draft: 0,
    };
    for (const c of campaigns) {
      if (c.status in counts) counts[c.status] += 1;
    }
    return counts;
  }, [campaigns]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return campaigns.filter((c) => {
      if (activeTab !== "all" && c.status !== activeTab) return false;
      if (!q) return true;
      return (
        c.name?.toLowerCase().includes(q) ||
        c.createdBy?.toLowerCase().includes(q)
      );
    });
  }, [campaigns, search, activeTab]);

  const STATUS_TABS = [
    { id: "all", label: "All" },
    { id: "active", label: "Active" },
    { id: "pending", label: "Pending" },
    { id: "completed", label: "Completed" },
    { id: "draft", label: "Draft" },
  ];

  const handleCreate = () => {
    navigate("/dashboard/campaigns/new");
  };

  const handleImported = (campaign) => {
    setImportOpen(false);
    refreshCampaigns();
    navigate(`/dashboard/campaigns/${campaign.uuid}/edit`, {
      state: { initialStep: 1 },
    });
  };

  const handleEdit = (campaign) => {
    navigate(`/dashboard/campaigns/${campaign.uuid}/edit`);
  };

  const handleDelete = async (campaign) => {
    const confirmed =
      typeof window !== "undefined"
        ? window.confirm(`Delete campaign "${campaign.name}"?`)
        : true;

    if (!confirmed) return;

    try {
      await deleteCampaign(campaign.uuid);
    } catch (error) {
      console.error("Failed to delete campaign", error);
      if (typeof window !== "undefined") {
        window.alert("Failed to delete campaign. Please try again.");
      }
    }
  };

  const handleApprove = async (campaign) => {
    if (!window.confirm(`Approve campaign "${campaign.name}"?`)) return;
    try {
      await approveCampaign(campaign.uuid);
      window.alert(`Campaign "${campaign.name}" approved.`);
    } catch (error) {
      console.error("Failed to approve campaign", error);
      window.alert("Failed to approve: " + (error.message || "Unknown error"));
    }
  };

  const loadCampaignStatistics = useCallback(
    async (campaignUuid) => {
      if (
        !campaignUuid ||
        loadingStats.has(campaignUuid) ||
        requestedStatsRef.current.has(campaignUuid)
      ) {
        return;
      }

      requestedStatsRef.current.add(campaignUuid);
      const cached = getCachedCampaignStatistics(campaignUuid);
      if (cached) {
        setCachedUuids((prev) =>
          prev.has(campaignUuid) ? prev : new Set(prev).add(campaignUuid),
        );
        setCampaignStats((prev) =>
          prev[campaignUuid] ? prev : { ...prev, [campaignUuid]: cached },
        );
      } else {
        setLoadingStats((prev) => new Set(prev).add(campaignUuid));
      }
      try {
        const stats = await getCampaignStatistics(campaignUuid);
        setCampaignStats((prev) => ({ ...prev, [campaignUuid]: stats }));
      } catch (error) {
        console.error(
          `Failed to load statistics for campaign ${campaignUuid}`,
          error,
        );
        setCampaignStats((prev) => ({
          ...prev,
          [campaignUuid]: {
            totalReceivers: 0,
            uniqueReceivers: 0,
            served: 0,
            spent: 0,
            confirmedImpressions: 0,
            views: 0,
          },
        }));
      } finally {
        setLoadingStats((prev) => {
          const next = new Set(prev);
          next.delete(campaignUuid);
          return next;
        });
      }
    },
    [getCampaignStatistics, getCachedCampaignStatistics, loadingStats],
  );

  useEffect(() => {
    if (campaigns.length > 0 && !campaignsLoading) {
      campaigns.forEach((campaign, index) => {
        if (campaign.uuid && !requestedStatsRef.current.has(campaign.uuid)) {
          setTimeout(() => {
            loadCampaignStatistics(campaign.uuid);
          }, index * 50);
        }
      });
    }
  }, [campaigns, campaignsLoading]);

  const showAuthor = currentUserType === "admin";

  return (
    <section>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search campaigns..."
        />
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setImportOpen(true)}
            className="inline-flex items-center gap-2 rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-2) px-[18px] py-[10px] text-[13px] font-bold text-(--text-primary) outline-none transition-colors hover:bg-(--surface-muted) focus:outline-none"
          >
            <Download size={16} strokeWidth={2.5} />
            Import
          </button>
          <button
            type="button"
            onClick={handleCreate}
            className="inline-flex items-center gap-2 rounded-(--radius-md) border-0 bg-(--brand-primary) px-[18px] py-[10px] text-[13px] font-bold text-(--brand-primary-contrast) outline-none transition-colors hover:bg-(--brand-primary-hover) focus:outline-none"
          >
            <Plus size={16} strokeWidth={2.5} />
            Create campaign
          </button>
        </div>
      </div>

      <TransferCampaignModal
        campaign={transferring}
        onClose={() => setTransferring(null)}
      />

      <ImportCampaignModal
        isOpen={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={handleImported}
      />

      <nav className="mb-5 flex gap-1 overflow-x-auto overflow-y-hidden border-b border-(--border-subtle)">
        {STATUS_TABS.map((tab) => {
          const isActive = activeTab === tab.id;
          const count = tabCounts[tab.id] ?? 0;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`relative flex items-center gap-2 whitespace-nowrap border-0 bg-transparent px-4 py-[11px] text-[13.5px] transition-colors ${
                isActive
                  ? "font-bold text-(--brand-primary)"
                  : "font-semibold text-(--text-secondary) hover:text-(--text-primary)"
              }`}
            >
              {tab.label}
              {count > 0 && (
                <span className="text-[12px] font-semibold text-(--text-muted)">
                  {count}
                </span>
              )}
              {isActive && (
                <span className="absolute inset-x-3 bottom-0 h-[2px] rounded-full bg-(--brand-primary)" />
              )}
            </button>
          );
        })}
      </nav>

      {campaignsError && campaigns.length === 0 ? (
        <Card>
          <h3 className="text-base font-semibold text-(--text-primary)">
            Unable to load campaigns
          </h3>
          <p className="mt-1 text-sm text-(--text-secondary)">
            Please refresh or try again later.
          </p>
          <div className="mt-3">
            <Button variant="secondary" onClick={refreshCampaigns}>
              Retry
            </Button>
          </div>
        </Card>
      ) : campaignsLoading && campaigns.length === 0 ? (
        <Card>
          <p className="text-sm text-(--text-secondary)">Loading campaigns…</p>
        </Card>
      ) : (
        <CampaignsTable
          campaigns={filtered}
          users={users}
          segments={segments}
          stats={campaignStats}
          loadingUuids={loadingStats}
          cachedUuids={cachedUuids}
          showAuthor={showAuthor}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onApprove={currentUserType === "admin" ? handleApprove : undefined}
          onTransfer={currentUserType === "admin" ? setTransferring : undefined}
          emptyMessage={
            search
              ? "No campaigns match your search."
              : activeTab !== "all"
                ? `No ${activeTab} campaigns.`
                : "No campaigns yet. Create your first campaign to get started."
          }
        />
      )}
    </section>
  );
}
